/**
 * Widget translations for offline form and system messages
 * Uses locale from widget config (defaults to 'en')
 */

export interface WidgetTranslations {
  // Offline form
  offline_title: string;
  offline_subtitle: string;
  email_label: string;
  email_placeholder: string;
  name_label: string;
  name_placeholder: string;
  message_label: string;
  message_placeholder: string;
  submit_button: string;

  // Success/Error messages
  success_title: string;
  success_message: string;
  success_message_with_email: string;
  error_message: string;

  // Validation
  email_validation_error: string;
  email_verifying: string;
  email_verified: string;
  email_domain_invalid: string;

  // Attachment toasts (paste/drop). Optional: locales that omit them fall back
  // to English via getTranslations() merging the English base. %{name}/%{names}
  // are interpolated with t().
  attachment_too_large?: string;
  attachment_too_many?: string;
  attachment_unsupported?: string;
  attachment_upload_failed?: string;
  attachment_validation_error?: string;
  attachment_send_failed?: string;
  attachment_retry?: string;
  attachment_retry_aria?: string;

  // Business hours (offline form). Optional: locales without them fall back to en.
  bh_next_opening?: string;
  bh_next_opening_local?: string;
  bh_hours_label?: string;
  bh_closed?: string;
  bh_timezone_note?: string;
}

const translations: Record<string, WidgetTranslations> = {
  en: {
    offline_title: "Agents unavailable",
    offline_subtitle: "Leave your email - we'll get back to you",
    email_label: "Email *",
    email_placeholder: "your@email.com",
    name_label: "Name (optional)",
    name_placeholder: "John Doe",
    message_label: "Message (optional)",
    message_placeholder: "How can we help?",
    submit_button: "Send",
    success_title: "Thank you!",
    success_message: "We'll notify you by email.",
    success_message_with_email: "Thank you! We received your message.<br>We'll reply to: <strong>%{email}</strong>",
    error_message: "An error occurred. Please try again.",
    email_validation_error: "Please enter a valid email address",
    email_verifying: "Verifying...",
    email_verified: "Email verified",
    email_domain_invalid: "This email domain does not exist",
    attachment_too_large: "%{name}: file is too large (max 10 MB)",
    attachment_too_many: "You can add up to 3 files at once",
    attachment_unsupported: "%{name}: this file type is not supported",
    attachment_upload_failed: "%{name}: failed to upload the file",
    attachment_validation_error: "%{name}: validation error",
    attachment_send_failed: "Failed to send: %{names}. Click Retry on the file.",
    attachment_retry: "Retry",
    attachment_retry_aria: "Retry upload %{name}",
    bh_next_opening: "We'll be back %{day} at %{time}",
    bh_next_opening_local: "Your time: %{day} %{time}",
    bh_hours_label: "Business hours",
    bh_closed: "Closed",
    bh_timezone_note: "Hours shown in %{site_tz}. Your time zone: %{local_tz}."
  },
  ar: {
    offline_title: "الوكلاء غير متاحين",
    offline_subtitle: "اترك بريدك الإلكتروني وسنعاود الاتصال بك",
    email_label: "البريد الإلكتروني *",
    email_placeholder: "your@email.com",
    name_label: "الاسم (اختياري)",
    name_placeholder: "محمد أحمد",
    message_label: "الرسالة (اختياري)",
    message_placeholder: "كيف يمكننا مساعدتك؟",
    submit_button: "إرسال",
    success_title: "شكرًا لك!",
    success_message: "سنخطرك عبر البريد الإلكتروني.",
    success_message_with_email: "شكرًا لك! لقد استلمنا رسالتك.<br>سنرد على: <strong>%{email}</strong>",
    error_message: "حدث خطأ. يرجى المحاولة مرة أخرى.",
    email_validation_error: "يرجى إدخال عنوان بريد إلكتروني صالح",
    email_verifying: "جارٍ التحقق...",
    email_verified: "تم التحقق من البريد الإلكتروني",
    email_domain_invalid: "نطاق البريد الإلكتروني هذا غير موجود",
    attachment_too_large: "%{name}: الملف كبير جدًا (الحد الأقصى 10 ميجابايت)",
    attachment_too_many: "يمكنك إضافة 3 ملفات كحد أقصى في المرة الواحدة",
    attachment_unsupported: "%{name}: نوع الملف هذا غير مدعوم",
    attachment_upload_failed: "%{name}: فشل تحميل الملف",
    attachment_validation_error: "%{name}: خطأ في التحقق",
    attachment_send_failed: "فشل الإرسال: %{names}. انقر على إعادة المحاولة على الملف.",
    attachment_retry: "إعادة المحاولة",
    attachment_retry_aria: "إعادة محاولة تحميل %{name}",
    bh_next_opening: "سنعود %{day} في تمام %{time}",
    bh_next_opening_local: "توقيتك: %{day} %{time}",
    bh_hours_label: "ساعات العمل",
    bh_closed: "مغلق",
    bh_timezone_note: "الأوقات بتوقيت %{site_tz}. منطقتك الزمنية: %{local_tz}."
  },
  pl: {
    offline_title: "Konsultanci niedostępni",
    offline_subtitle: "Zostaw email - odezwiemy się gdy wrócimy",
    email_label: "Email *",
    email_placeholder: "twoj@email.pl",
    name_label: "Imię (opcjonalne)",
    name_placeholder: "Jan Kowalski",
    message_label: "Wiadomość (opcjonalna)",
    message_placeholder: "W czym możemy pomóc?",
    submit_button: "Wyślij",
    success_title: "Dziękujemy!",
    success_message: "Powiadomimy Cię mailowo.",
    success_message_with_email: "Dziękujemy! Otrzymaliśmy Twoją wiadomość.<br>Odpowiemy na: <strong>%{email}</strong>",
    error_message: "Wystąpił błąd. Spróbuj ponownie.",
    email_validation_error: "Proszę podać poprawny adres email",
    email_verifying: "Weryfikuję...",
    email_verified: "Email zweryfikowany",
    email_domain_invalid: "Ta domena email nie istnieje",
    attachment_too_large: "%{name}: plik jest za duży (maks. 10 MB)",
    attachment_too_many: "Można dodać maksymalnie 3 pliki naraz",
    attachment_unsupported: "%{name}: ten typ pliku nie jest obsługiwany",
    attachment_upload_failed: "%{name}: nie udało się wysłać pliku",
    attachment_validation_error: "%{name}: błąd walidacji",
    attachment_send_failed: "Nie udało się wysłać: %{names}. Kliknij Ponów przy pliku.",
    attachment_retry: "Ponów",
    attachment_retry_aria: "Ponów wysyłkę %{name}",
    bh_next_opening: "Wrócimy %{day} o %{time}",
    bh_next_opening_local: "U Ciebie: %{day} %{time}",
    bh_hours_label: "Godziny pracy",
    bh_closed: "Nieczynne",
    bh_timezone_note: "Godziny w strefie %{site_tz}. Twoja strefa: %{local_tz}."
  },
  cs: {
    offline_title: "Konzultanti nejsou k dispozici",
    offline_subtitle: "Zanechte email - ozveme se vám",
    email_label: "Email *",
    email_placeholder: "vas@email.cz",
    name_label: "Jméno (volitelné)",
    name_placeholder: "Jan Novák",
    message_label: "Zpráva (volitelná)",
    message_placeholder: "Jak vám můžeme pomoci?",
    submit_button: "Odeslat",
    success_title: "Děkujeme!",
    success_message: "Budeme vás informovat e-mailem.",
    success_message_with_email: "Děkujeme! Obdrželi jsme vaši zprávu.<br>Odpovíme na: <strong>%{email}</strong>",
    error_message: "Došlo k chybě. Zkuste to prosím znovu.",
    email_validation_error: "Zadejte prosím platnou e-mailovou adresu",
    email_verifying: "Ověřuji...",
    email_verified: "E-mail ověřen",
    email_domain_invalid: "Tato e-mailová doména neexistuje",
    attachment_too_large: "%{name}: soubor je příliš velký (max. 10 MB)",
    attachment_too_many: "Najednou můžete přidat nejvýše 3 soubory",
    attachment_unsupported: "%{name}: tento typ souboru není podporován",
    attachment_upload_failed: "%{name}: soubor se nepodařilo nahrát",
    attachment_validation_error: "%{name}: chyba ověření",
    attachment_send_failed: "Nepodařilo se odeslat: %{names}. Klikněte na Zkusit znovu u souboru.",
    attachment_retry: "Zkusit znovu",
    attachment_retry_aria: "Zkusit znovu nahrát %{name}",
    bh_next_opening: "Budeme zpět %{day} v %{time}",
    bh_next_opening_local: "Váš čas: %{day} %{time}",
    bh_hours_label: "Pracovní doba",
    bh_closed: "Zavřeno",
    bh_timezone_note: "Hodiny jsou v %{site_tz}. Vaše časové pásmo: %{local_tz}."
  },
  de: {
    offline_title: "Berater nicht verfügbar",
    offline_subtitle: "Hinterlassen Sie Ihre E-Mail - wir melden uns bei Ihnen",
    email_label: "E-Mail *",
    email_placeholder: "ihre@email.de",
    name_label: "Name (optional)",
    name_placeholder: "Max Mustermann",
    message_label: "Nachricht (optional)",
    message_placeholder: "Wie können wir helfen?",
    submit_button: "Senden",
    success_title: "Vielen Dank!",
    success_message: "Wir benachrichtigen Sie per E-Mail.",
    success_message_with_email: "Vielen Dank! Wir haben Ihre Nachricht erhalten.<br>Wir antworten an: <strong>%{email}</strong>",
    error_message: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
    email_validation_error: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    email_verifying: "Überprüfe...",
    email_verified: "E-Mail verifiziert",
    email_domain_invalid: "Diese E-Mail-Domain existiert nicht",
    bh_next_opening: "Wir sind %{day} um %{time} zurück",
    bh_next_opening_local: "Ihre Zeit: %{day} %{time}",
    bh_hours_label: "Geschäftszeiten",
    bh_closed: "Geschlossen",
    bh_timezone_note: "Zeiten in %{site_tz}. Ihre Zeitzone: %{local_tz}."
  },
  nl: {
    offline_title: "Medewerkers niet beschikbaar",
    offline_subtitle: "Laat uw e-mail achter - we nemen contact met u op",
    email_label: "E-mail *",
    email_placeholder: "uw@email.nl",
    name_label: "Naam (optioneel)",
    name_placeholder: "Jan Jansen",
    message_label: "Bericht (optioneel)",
    message_placeholder: "Hoe kunnen we u helpen?",
    submit_button: "Verzenden",
    success_title: "Bedankt!",
    success_message: "We sturen u een e-mail.",
    success_message_with_email: "Bedankt! We hebben uw bericht ontvangen.<br>We antwoorden naar: <strong>%{email}</strong>",
    error_message: "Er is een fout opgetreden. Probeer het opnieuw.",
    email_validation_error: "Voer een geldig e-mailadres in",
    email_verifying: "Verifiëren...",
    email_verified: "E-mail geverifieerd",
    email_domain_invalid: "Dit e-maildomein bestaat niet",
    bh_next_opening: "We zijn terug %{day} om %{time}",
    bh_next_opening_local: "Uw tijd: %{day} %{time}",
    bh_hours_label: "Openingstijden",
    bh_closed: "Gesloten",
    bh_timezone_note: "Tijden in %{site_tz}. Uw tijdzone: %{local_tz}."
  },
  fr: {
    offline_title: "Conseillers indisponibles",
    offline_subtitle: "Laissez votre e-mail - nous vous recontacterons",
    email_label: "E-mail *",
    email_placeholder: "votre@email.fr",
    name_label: "Nom (facultatif)",
    name_placeholder: "Jean Dupont",
    message_label: "Message (facultatif)",
    message_placeholder: "Comment pouvons-nous vous aider ?",
    submit_button: "Envoyer",
    success_title: "Merci !",
    success_message: "Nous vous contacterons par e-mail.",
    success_message_with_email: "Merci ! Nous avons reçu votre message.<br>Nous répondrons à : <strong>%{email}</strong>",
    error_message: "Une erreur s'est produite. Veuillez réessayer.",
    email_validation_error: "Veuillez entrer une adresse e-mail valide",
    email_verifying: "Vérification...",
    email_verified: "E-mail vérifié",
    email_domain_invalid: "Ce domaine e-mail n'existe pas",
    bh_next_opening: "Nous serons de retour %{day} à %{time}",
    bh_next_opening_local: "Votre heure : %{day} %{time}",
    bh_hours_label: "Heures d'ouverture",
    bh_closed: "Fermé",
    bh_timezone_note: "Heures en %{site_tz}. Votre fuseau horaire : %{local_tz}."
  },
  es: {
    offline_title: "Agentes no disponibles",
    offline_subtitle: "Deja tu correo electrónico - te contactaremos",
    email_label: "Correo electrónico *",
    email_placeholder: "tu@email.es",
    name_label: "Nombre (opcional)",
    name_placeholder: "Juan García",
    message_label: "Mensaje (opcional)",
    message_placeholder: "¿Cómo podemos ayudarte?",
    submit_button: "Enviar",
    success_title: "¡Gracias!",
    success_message: "Te notificaremos por correo electrónico.",
    success_message_with_email: "¡Gracias! Hemos recibido tu mensaje.<br>Responderemos a: <strong>%{email}</strong>",
    error_message: "Ha ocurrido un error. Por favor, inténtalo de nuevo.",
    email_validation_error: "Por favor, introduce una dirección de correo válida",
    email_verifying: "Verificando...",
    email_verified: "Correo verificado",
    email_domain_invalid: "Este dominio de correo no existe",
    bh_next_opening: "Volveremos el %{day} a las %{time}",
    bh_next_opening_local: "Tu hora: %{day} %{time}",
    bh_hours_label: "Horario de atención",
    bh_closed: "Cerrado",
    bh_timezone_note: "Horarios en %{site_tz}. Tu zona horaria: %{local_tz}."
  },
  pt: {
    offline_title: "Agentes indisponíveis",
    offline_subtitle: "Deixe seu e-mail - entraremos em contato",
    email_label: "E-mail *",
    email_placeholder: "seu@email.pt",
    name_label: "Nome (opcional)",
    name_placeholder: "João Silva",
    message_label: "Mensagem (opcional)",
    message_placeholder: "Como podemos ajudar?",
    submit_button: "Enviar",
    success_title: "Obrigado!",
    success_message: "Enviaremos uma notificação por e-mail.",
    success_message_with_email: "Obrigado! Recebemos sua mensagem.<br>Responderemos para: <strong>%{email}</strong>",
    error_message: "Ocorreu um erro. Por favor, tente novamente.",
    email_validation_error: "Por favor, insira um endereço de e-mail válido",
    email_verifying: "Verificando...",
    email_verified: "E-mail verificado",
    email_domain_invalid: "Este domínio de e-mail não existe",
    bh_next_opening: "Voltaremos %{day} às %{time}",
    bh_next_opening_local: "Seu horário: %{day} %{time}",
    bh_hours_label: "Horário de atendimento",
    bh_closed: "Fechado",
    bh_timezone_note: "Horários em %{site_tz}. Seu fuso horário: %{local_tz}."
  },
  sk: {
    offline_title: "Konzultanti nie sú k dispozícii",
    offline_subtitle: "Zanechajte e-mail - ozveme sa vám",
    email_label: "E-mail *",
    email_placeholder: "vas@email.sk",
    name_label: "Meno (voliteľné)",
    name_placeholder: "Ján Novák",
    message_label: "Správa (voliteľná)",
    message_placeholder: "Ako vám môžeme pomôcť?",
    submit_button: "Odoslať",
    success_title: "Ďakujeme!",
    success_message: "Budeme vás informovať e-mailom.",
    success_message_with_email: "Ďakujeme! Dostali sme vašu správu.<br>Odpovieme na: <strong>%{email}</strong>",
    error_message: "Vyskytla sa chyba. Skúste to prosím znova.",
    email_validation_error: "Zadajte prosím platnú e-mailovú adresu",
    email_verifying: "Overujem...",
    email_verified: "E-mail overený",
    email_domain_invalid: "Táto e-mailová doména neexistuje",
    bh_next_opening: "Budeme späť %{day} o %{time}",
    bh_next_opening_local: "Váš čas: %{day} %{time}",
    bh_hours_label: "Pracovná doba",
    bh_closed: "Zatvorené",
    bh_timezone_note: "Hodiny sú v %{site_tz}. Vaše časové pásmo: %{local_tz}."
  },
  hr: {
    offline_title: "Agenti nisu dostupni",
    offline_subtitle: "Ostavite svoj e-mail - javit ćemo vam se",
    email_label: "E-mail *",
    email_placeholder: "vas@email.hr",
    name_label: "Ime (neobavezno)",
    name_placeholder: "Ivan Horvat",
    message_label: "Poruka (neobavezno)",
    message_placeholder: "Kako vam možemo pomoći?",
    submit_button: "Pošalji",
    success_title: "Hvala!",
    success_message: "Obavijestit ćemo vas e-mailom.",
    success_message_with_email: "Hvala! Primili smo vašu poruku.<br>Odgovorit ćemo na: <strong>%{email}</strong>",
    error_message: "Došlo je do greške. Molimo pokušajte ponovo.",
    email_validation_error: "Molimo unesite valjanu e-mail adresu",
    email_verifying: "Provjeravam...",
    email_verified: "E-mail verificiran",
    email_domain_invalid: "Ova e-mail domena ne postoji",
    bh_next_opening: "Vraćamo se %{day} u %{time}",
    bh_next_opening_local: "Vaše vrijeme: %{day} %{time}",
    bh_hours_label: "Radno vrijeme",
    bh_closed: "Zatvoreno",
    bh_timezone_note: "Sati su u %{site_tz}. Vaša vremenska zona: %{local_tz}."
  },
  lt: {
    offline_title: "Konsultantai nepasiekiami",
    offline_subtitle: "Palikite el. paštą - susisieksime su jumis",
    email_label: "El. paštas *",
    email_placeholder: "jusu@email.lt",
    name_label: "Vardas (neprivaloma)",
    name_placeholder: "Jonas Jonaitis",
    message_label: "Žinutė (neprivaloma)",
    message_placeholder: "Kaip galime padėti?",
    submit_button: "Siųsti",
    success_title: "Ačiū!",
    success_message: "Informuosime jus el. paštu.",
    success_message_with_email: "Ačiū! Gavome jūsų žinutę.<br>Atsakysime adresu: <strong>%{email}</strong>",
    error_message: "Įvyko klaida. Bandykite dar kartą.",
    email_validation_error: "Įveskite galiojantį el. pašto adresą",
    email_verifying: "Tikrinama...",
    email_verified: "El. paštas patvirtintas",
    email_domain_invalid: "Šis el. pašto domenas neegzistuoja",
    bh_next_opening: "Grįšime %{day} %{time}",
    bh_next_opening_local: "Jūsų laikas: %{day} %{time}",
    bh_hours_label: "Darbo laikas",
    bh_closed: "Uždaryta",
    bh_timezone_note: "Laikas pagal %{site_tz}. Jūsų laiko juosta: %{local_tz}."
  },
  lv: {
    offline_title: "Konsultanti nav pieejami",
    offline_subtitle: "Atstājiet savu e-pastu - mēs ar jums sazināsimies",
    email_label: "E-pasts *",
    email_placeholder: "jusu@email.lv",
    name_label: "Vārds (neobligāti)",
    name_placeholder: "Jānis Bērziņš",
    message_label: "Ziņojums (neobligāti)",
    message_placeholder: "Kā mēs varam palīdzēt?",
    submit_button: "Nosūtīt",
    success_title: "Paldies!",
    success_message: "Mēs jūs informēsim pa e-pastu.",
    success_message_with_email: "Paldies! Mēs saņēmām jūsu ziņojumu.<br>Atbildēsim uz: <strong>%{email}</strong>",
    error_message: "Radās kļūda. Lūdzu, mēģiniet vēlreiz.",
    email_validation_error: "Lūdzu, ievadiet derīgu e-pasta adresi",
    email_verifying: "Pārbauda...",
    email_verified: "E-pasts apstiprināts",
    email_domain_invalid: "Šis e-pasta domēns neeksistē",
    bh_next_opening: "Atgriezīsimies %{day} plkst. %{time}",
    bh_next_opening_local: "Jūsu laiks: %{day} %{time}",
    bh_hours_label: "Darba laiks",
    bh_closed: "Slēgts",
    bh_timezone_note: "Laiks pēc %{site_tz}. Jūsu laika zona: %{local_tz}."
  },
  et: {
    offline_title: "Konsultandid pole saadaval",
    offline_subtitle: "Jätke oma e-post - võtame teiega ühendust",
    email_label: "E-post *",
    email_placeholder: "teie@email.ee",
    name_label: "Nimi (valikuline)",
    name_placeholder: "Jaan Tamm",
    message_label: "Sõnum (valikuline)",
    message_placeholder: "Kuidas saame aidata?",
    submit_button: "Saada",
    success_title: "Täname!",
    success_message: "Teavitame teid e-posti teel.",
    success_message_with_email: "Täname! Saime teie sõnumi kätte.<br>Vastame aadressil: <strong>%{email}</strong>",
    error_message: "Tekkis viga. Palun proovige uuesti.",
    email_validation_error: "Palun sisestage kehtiv e-posti aadress",
    email_verifying: "Kontrollin...",
    email_verified: "E-post kinnitatud",
    email_domain_invalid: "See e-posti domeen ei eksisteeri",
    bh_next_opening: "Oleme tagasi %{day} kell %{time}",
    bh_next_opening_local: "Teie aeg: %{day} %{time}",
    bh_hours_label: "Tööaeg",
    bh_closed: "Suletud",
    bh_timezone_note: "Ajad %{site_tz} järgi. Teie ajavöönd: %{local_tz}."
  },
  bg: {
    offline_title: "Консултантите не са на линия",
    offline_subtitle: "Оставете имейл - ще се свържем с вас",
    email_label: "Имейл *",
    email_placeholder: "vashiat@email.bg",
    name_label: "Име (по избор)",
    name_placeholder: "Иван Иванов",
    message_label: "Съобщение (по избор)",
    message_placeholder: "Как можем да помогнем?",
    submit_button: "Изпрати",
    success_title: "Благодарим!",
    success_message: "Ще ви уведомим по имейл.",
    success_message_with_email: "Благодарим! Получихме вашето съобщение.<br>Ще отговорим на: <strong>%{email}</strong>",
    error_message: "Възникна грешка. Моля, опитайте отново.",
    email_validation_error: "Моля, въведете валиден имейл адрес",
    email_verifying: "Проверка...",
    email_verified: "Имейлът е потвърден",
    email_domain_invalid: "Този имейл домейн не съществува",
    attachment_too_large: "%{name}: файлът е твърде голям (макс. 10 MB)",
    attachment_too_many: "Можете да добавите най-много 3 файла наведнъж",
    attachment_unsupported: "%{name}: този тип файл не се поддържа",
    attachment_upload_failed: "%{name}: файлът не можа да бъде качен",
    attachment_validation_error: "%{name}: грешка при валидация",
    attachment_send_failed: "Неуспешно изпращане: %{names}. Натиснете „Опитай отново“ при файла.",
    attachment_retry: "Опитай отново",
    attachment_retry_aria: "Повторно качване на %{name}",
    bh_next_opening: "Ще се върнем %{day} в %{time}",
    bh_next_opening_local: "Вашето време: %{day} %{time}",
    bh_hours_label: "Работно време",
    bh_closed: "Затворено",
    bh_timezone_note: "Часовете са в %{site_tz}. Вашата часова зона: %{local_tz}."
  },
  sl: {
    offline_title: "Svetovalci niso na voljo",
    offline_subtitle: "Pustite e-pošto - odgovorimo vam, ko se vrnemo",
    email_label: "E-pošta *",
    email_placeholder: "vas@email.si",
    name_label: "Ime (neobvezno)",
    name_placeholder: "Janez Novak",
    message_label: "Sporočilo (neobvezno)",
    message_placeholder: "Kako vam lahko pomagamo?",
    submit_button: "Pošlji",
    success_title: "Hvala!",
    success_message: "Obvestili vas bomo po e-pošti.",
    success_message_with_email: "Hvala! Prejeli smo vaše sporočilo.<br>Odgovorimo na: <strong>%{email}</strong>",
    error_message: "Prišlo je do napake. Poskusite znova.",
    email_validation_error: "Vnesite veljaven e-poštni naslov",
    email_verifying: "Preverjanje...",
    email_verified: "E-pošta potrjena",
    email_domain_invalid: "Ta e-poštna domena ne obstaja",
    attachment_too_large: "%{name}: datoteka je prevelika (največ 10 MB)",
    attachment_too_many: "Hkrati lahko dodate največ 3 datoteke",
    attachment_unsupported: "%{name}: ta vrsta datoteke ni podprta",
    attachment_upload_failed: "%{name}: nalaganje datoteke ni uspelo",
    attachment_validation_error: "%{name}: napaka pri preverjanju",
    attachment_send_failed: "Pošiljanje ni uspelo: %{names}. Pri datoteki kliknite Poskusi znova.",
    attachment_retry: "Poskusi znova",
    attachment_retry_aria: "Ponovno naloži %{name}",
    bh_next_opening: "Vrnemo se %{day} ob %{time}",
    bh_next_opening_local: "Vaš čas: %{day} %{time}",
    bh_hours_label: "Delovni čas",
    bh_closed: "Zaprto",
    bh_timezone_note: "Ure so v časovnem pasu %{site_tz}. Vaš časovni pas: %{local_tz}."
  },
  el: {
    offline_title: "Οι σύμβουλοι δεν είναι διαθέσιμοι",
    offline_subtitle: "Αφήστε το email σας - θα επικοινωνήσουμε μαζί σας",
    email_label: "Email *",
    email_placeholder: "to@email.gr",
    name_label: "Όνομα (προαιρετικό)",
    name_placeholder: "Γιώργος Παπαδόπουλος",
    message_label: "Μήνυμα (προαιρετικό)",
    message_placeholder: "Πώς μπορούμε να βοηθήσουμε;",
    submit_button: "Αποστολή",
    success_title: "Ευχαριστούμε!",
    success_message: "Θα σας ενημερώσουμε μέσω email.",
    success_message_with_email: "Ευχαριστούμε! Λάβαμε το μήνυμά σας.<br>Θα απαντήσουμε στο: <strong>%{email}</strong>",
    error_message: "Προέκυψε σφάλμα. Παρακαλούμε δοκιμάστε ξανά.",
    email_validation_error: "Παρακαλούμε εισάγετε μια έγκυρη διεύθυνση email",
    email_verifying: "Επαλήθευση...",
    email_verified: "Το email επαληθεύτηκε",
    email_domain_invalid: "Αυτός ο τομέας email δεν υπάρχει",
    attachment_too_large: "%{name}: το αρχείο είναι πολύ μεγάλο (έως 10 MB)",
    attachment_too_many: "Μπορείτε να προσθέσετε έως 3 αρχεία τη φορά",
    attachment_unsupported: "%{name}: αυτός ο τύπος αρχείου δεν υποστηρίζεται",
    attachment_upload_failed: "%{name}: η μεταφόρτωση του αρχείου απέτυχε",
    attachment_validation_error: "%{name}: σφάλμα επικύρωσης",
    attachment_send_failed: "Η αποστολή απέτυχε: %{names}. Πατήστε «Επανάληψη» στο αρχείο.",
    attachment_retry: "Επανάληψη",
    attachment_retry_aria: "Επανάληψη μεταφόρτωσης %{name}",
    bh_next_opening: "Επιστρέφουμε %{day} στις %{time}",
    bh_next_opening_local: "Η ώρα σας: %{day} %{time}",
    bh_hours_label: "Ώρες λειτουργίας",
    bh_closed: "Κλειστά",
    bh_timezone_note: "Οι ώρες εμφανίζονται σε %{site_tz}. Η ζώνη ώρας σας: %{local_tz}."
  },
  it: {
    offline_title: "Operatori non disponibili",
    offline_subtitle: "Lascia la tua e-mail - ti ricontatteremo",
    email_label: "E-mail *",
    email_placeholder: "tua@email.it",
    name_label: "Nome (facoltativo)",
    name_placeholder: "Mario Rossi",
    message_label: "Messaggio (facoltativo)",
    message_placeholder: "Come possiamo aiutarti?",
    submit_button: "Invia",
    success_title: "Grazie!",
    success_message: "Ti avviseremo via e-mail.",
    success_message_with_email: "Grazie! Abbiamo ricevuto il tuo messaggio.<br>Risponderemo a: <strong>%{email}</strong>",
    error_message: "Si è verificato un errore. Riprova.",
    email_validation_error: "Inserisci un indirizzo e-mail valido",
    email_verifying: "Verifica...",
    email_verified: "E-mail verificata",
    email_domain_invalid: "Questo dominio e-mail non esiste",
    bh_next_opening: "Saremo di nuovo disponibili %{day} alle %{time}",
    bh_next_opening_local: "Ora locale: %{day} %{time}",
    bh_hours_label: "Orari di apertura",
    bh_closed: "Chiuso",
    bh_timezone_note: "Orari in %{site_tz}. Il tuo fuso orario: %{local_tz}."
  },
  ru: {
    offline_title: "Консультанты недоступны",
    offline_subtitle: "Оставьте ваш e-mail - мы свяжемся с вами",
    email_label: "E-mail *",
    email_placeholder: "vash@email.ru",
    name_label: "Имя (необязательно)",
    name_placeholder: "Иван Иванов",
    message_label: "Сообщение (необязательно)",
    message_placeholder: "Чем мы можем помочь?",
    submit_button: "Отправить",
    success_title: "Спасибо!",
    success_message: "Мы уведомим вас по e-mail.",
    success_message_with_email: "Спасибо! Мы получили ваше сообщение.<br>Ответим на: <strong>%{email}</strong>",
    error_message: "Произошла ошибка. Попробуйте ещё раз.",
    email_validation_error: "Пожалуйста, введите действительный адрес e-mail",
    email_verifying: "Проверка...",
    email_verified: "E-mail подтверждён",
    email_domain_invalid: "Этот домен e-mail не существует",
    bh_next_opening: "Мы вернёмся %{day} в %{time}",
    bh_next_opening_local: "Ваше время: %{day} %{time}",
    bh_hours_label: "Часы работы",
    bh_closed: "Закрыто",
    bh_timezone_note: "Время указано по %{site_tz}. Ваш часовой пояс: %{local_tz}."
  },
  da: {
    offline_title: "Konsulenter er ikke tilgængelige",
    offline_subtitle: "Efterlad din e-mail - vi kontakter dig",
    email_label: "E-mail *",
    email_placeholder: "din@email.dk",
    name_label: "Navn (valgfrit)",
    name_placeholder: "Anders Andersen",
    message_label: "Besked (valgfrit)",
    message_placeholder: "Hvordan kan vi hjælpe?",
    submit_button: "Send",
    success_title: "Tak!",
    success_message: "Vi giver dig besked via e-mail.",
    success_message_with_email: "Tak! Vi har modtaget din besked.<br>Vi svarer til: <strong>%{email}</strong>",
    error_message: "Der opstod en fejl. Prøv venligst igen.",
    email_validation_error: "Indtast venligst en gyldig e-mailadresse",
    email_verifying: "Verificerer...",
    email_verified: "E-mail bekræftet",
    email_domain_invalid: "Dette e-maildomæne eksisterer ikke",
    bh_next_opening: "Vi er tilbage %{day} kl. %{time}",
    bh_next_opening_local: "Din tid: %{day} %{time}",
    bh_hours_label: "Åbningstider",
    bh_closed: "Lukket",
    bh_timezone_note: "Tider i %{site_tz}. Din tidszone: %{local_tz}."
  },
  fi: {
    offline_title: "Konsultit eivät ole käytettävissä",
    offline_subtitle: "Jätä sähköpostiosoitteesi - otamme sinuun yhteyttä",
    email_label: "Sähköposti *",
    email_placeholder: "sinun@email.fi",
    name_label: "Nimi (valinnainen)",
    name_placeholder: "Matti Meikäläinen",
    message_label: "Viesti (valinnainen)",
    message_placeholder: "Miten voimme auttaa?",
    submit_button: "Lähetä",
    success_title: "Kiitos!",
    success_message: "Ilmoitamme sinulle sähköpostitse.",
    success_message_with_email: "Kiitos! Olemme vastaanottaneet viestisi.<br>Vastaamme osoitteeseen: <strong>%{email}</strong>",
    error_message: "Tapahtui virhe. Yritä uudelleen.",
    email_validation_error: "Anna kelvollinen sähköpostiosoite",
    email_verifying: "Tarkistetaan...",
    email_verified: "Sähköposti vahvistettu",
    email_domain_invalid: "Tätä sähköpostiverkkotunnusta ei ole olemassa",
    bh_next_opening: "Olemme takaisin %{day} klo %{time}",
    bh_next_opening_local: "Aikasi: %{day} %{time}",
    bh_hours_label: "Aukioloajat",
    bh_closed: "Suljettu",
    bh_timezone_note: "Ajat %{site_tz}:n mukaan. Aikavyöhykkeesi: %{local_tz}."
  },
  hu: {
    offline_title: "Tanácsadók nem elérhetők",
    offline_subtitle: "Hagyja meg e-mail címét - felvesszük Önnel a kapcsolatot",
    email_label: "E-mail *",
    email_placeholder: "az.on@email.hu",
    name_label: "Név (opcionális)",
    name_placeholder: "Kovács János",
    message_label: "Üzenet (opcionális)",
    message_placeholder: "Miben segíthetünk?",
    submit_button: "Küldés",
    success_title: "Köszönjük!",
    success_message: "E-mailben értesítjük.",
    success_message_with_email: "Köszönjük! Megkaptuk üzenetét.<br>Válaszolunk erre a címre: <strong>%{email}</strong>",
    error_message: "Hiba történt. Kérjük, próbálja újra.",
    email_validation_error: "Kérjük, adjon meg egy érvényes e-mail címet",
    email_verifying: "Ellenőrzés...",
    email_verified: "E-mail ellenőrizve",
    email_domain_invalid: "Ez az e-mail domain nem létezik",
    bh_next_opening: "Visszatérünk %{day} %{time} órakor",
    bh_next_opening_local: "Az ön ideje: %{day} %{time}",
    bh_hours_label: "Munkaidő",
    bh_closed: "Zárva",
    bh_timezone_note: "Az időpontok %{site_tz} szerint. Az ön időzónája: %{local_tz}."
  },
  ro: {
    offline_title: "Consultanții nu sunt disponibili",
    offline_subtitle: "Lăsați adresa de e-mail - vă vom contacta",
    email_label: "E-mail *",
    email_placeholder: "adresa@email.ro",
    name_label: "Nume (opțional)",
    name_placeholder: "Ion Popescu",
    message_label: "Mesaj (opțional)",
    message_placeholder: "Cum vă putem ajuta?",
    submit_button: "Trimite",
    success_title: "Mulțumim!",
    success_message: "Vă vom notifica prin e-mail.",
    success_message_with_email: "Mulțumim! Am primit mesajul dumneavoastră.<br>Vom răspunde la: <strong>%{email}</strong>",
    error_message: "A apărut o eroare. Vă rugăm să încercați din nou.",
    email_validation_error: "Vă rugăm să introduceți o adresă de e-mail validă",
    email_verifying: "Verificare...",
    email_verified: "E-mail verificat",
    email_domain_invalid: "Acest domeniu de e-mail nu există",
    bh_next_opening: "Vom fi înapoi %{day} la ora %{time}",
    bh_next_opening_local: "Ora dvs.: %{day} %{time}",
    bh_hours_label: "Program de lucru",
    bh_closed: "Închis",
    bh_timezone_note: "Orele sunt în %{site_tz}. Fusul dvs. orar: %{local_tz}."
  },
  sv: {
    offline_title: "Konsulter är inte tillgängliga",
    offline_subtitle: "Lämna din e-post - vi kontaktar dig",
    email_label: "E-post *",
    email_placeholder: "din@email.se",
    name_label: "Namn (valfritt)",
    name_placeholder: "Erik Svensson",
    message_label: "Meddelande (valfritt)",
    message_placeholder: "Hur kan vi hjälpa dig?",
    submit_button: "Skicka",
    success_title: "Tack!",
    success_message: "Vi meddelar dig via e-post.",
    success_message_with_email: "Tack! Vi har tagit emot ditt meddelande.<br>Vi svarar till: <strong>%{email}</strong>",
    error_message: "Ett fel uppstod. Försök igen.",
    email_validation_error: "Ange en giltig e-postadress",
    email_verifying: "Verifierar...",
    email_verified: "E-post verifierad",
    email_domain_invalid: "Denna e-postdomän existerar inte",
    bh_next_opening: "Vi är tillbaka %{day} kl. %{time}",
    bh_next_opening_local: "Din tid: %{day} %{time}",
    bh_hours_label: "Öppettider",
    bh_closed: "Stängt",
    bh_timezone_note: "Tider i %{site_tz}. Din tidszon: %{local_tz}."
  }
};

/**
 * Get translations for a specific locale
 * Falls back to English if locale not found
 */
export function getTranslations(locale: string): WidgetTranslations {
  // Merge the chosen locale over the English base so any key a locale has not
  // translated yet falls back to English (never to another language). This also
  // makes adding new keys safe — only `en` is mandatory.
  const base = translations.en;

  // Try exact match first
  if (translations[locale]) {
    return { ...base, ...translations[locale] };
  }

  // Try language code only (e.g., "en-US" -> "en")
  const langCode = locale.split('-')[0].toLowerCase();
  if (translations[langCode]) {
    return { ...base, ...translations[langCode] };
  }

  // Default to English
  return base;
}

/**
 * Interpolate variables in translation string
 * Example: "%{email}" with {email: "test@test.com"} -> "test@test.com"
 */
export function t(text: string, vars?: Record<string, string>): string {
  if (!vars) return text;

  return Object.entries(vars).reduce((result, [key, value]) => {
    return result.replace(new RegExp(`%\\{${key}\\}`, 'g'), value);
  }, text);
}

/**
 * Raw locale tables — exported for native-presence tests only.
 * In application code always use getTranslations().
 */
export const localeTables: Record<string, Partial<WidgetTranslations>> = translations;
